import { useMemo } from 'react'
import {
  CircularProgress,
  Container,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { useGetProjectFeatures } from '@/service-library/hooks/project-features'
import {
  useCreateProjectProjectFeature,
  useDeleteProjectProjectFeature,
  useGetProjectProjectFeatures,
} from '@/service-library/hooks/project-project-features'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

export default function ProjectFeaturesPage() {
  const { project } = useProjectContext()

  const { projectFeatures, isPending: isPendingProjectFeatures } =
    useGetProjectFeatures({
      filters: { limit: '1000' },
    })

  const {
    projectProjectFeatures,
    isPending: isPendingProjectProjectFeatures,
    queryKey,
  } = useGetProjectProjectFeatures({
    filters: { project_id: project.id, limit: '1000' },
  })

  const { createProjectProjectFeature } = useCreateProjectProjectFeature({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to enable feature. Please try again later.')
    },
  })

  const { deleteProjectProjectFeature } = useDeleteProjectProjectFeature({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to disable feature. Please try again later.')
    },
  })

  const projectProjectFeatureIdByFeatureId = useMemo(
    () =>
      new Map(
        projectProjectFeatures.map((projectProjectFeature) => [
          projectProjectFeature.project_feature_id,
          projectProjectFeature.id,
        ]),
      ),
    [projectProjectFeatures],
  )

  const isPending = isPendingProjectFeatures || isPendingProjectProjectFeatures

  function handleToggle(projectFeatureId: string, checked: boolean) {
    const projectProjectFeatureId =
      projectProjectFeatureIdByFeatureId.get(projectFeatureId)
    if (checked) {
      if (!projectProjectFeatureId) {
        createProjectProjectFeature({
          id: generateUuid(),
          project_id: project.id,
          project_feature_id: projectFeatureId,
        })
      }
    } else {
      if (projectProjectFeatureId) {
        deleteProjectProjectFeature(projectProjectFeatureId)
      }
    }
  }

  return (
    <Container maxWidth="md">
      <ProjectBreadcrumb label="Features" url="../features" />

      <Stack spacing={2}>
        <Typography variant="h5">Features</Typography>

        {isPending ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <List>
            {projectFeatures.map((feature) => {
              const isEnabled = projectProjectFeatureIdByFeatureId.has(
                feature.id,
              )

              return (
                <ListItem disableGutters key={feature.id}>
                  <FormControlLabel
                    control={<Switch checked={isEnabled} sx={{ mr: 1 }} />}
                    label={
                      <>
                        <Typography>{feature.name}</Typography>
                        <Typography variant="caption">
                          {feature.description}
                        </Typography>
                      </>
                    }
                    onChange={(_e, checked) => {
                      handleToggle(feature.id, checked)
                    }}
                  />
                </ListItem>
              )
            })}
          </List>
        )}
      </Stack>
    </Container>
  )
}
